import React, { useEffect, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import videojs from "video.js/dist/video.es.js";
import "videojs-seek-buttons";
import { player as config, shortcuts } from "config";
import { EVENTS, ID } from "utils/constants";
import { buildFileURL } from "utils/data";
import logger from "utils/logger";
import { getFrequency, getTime } from "utils/params";
import storage from "utils/data/storage";
import player from "utils/player";
import "./index.scss";
import { EVENT_KEY, EventEmitter } from "utils/eventManager";
import { useLocation } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

const intlMessages = defineMessages({
  aria: {
    id: "player.webcams.wrapper.aria",
    description: "Aria label for the webcams wrapper",
  },
});

const POSITION_BLUR = {
  RIGHT: "right",
  LEFT: "left",
  FULL: "full",
  NONE: "none",
};

const mlsShowEarlyBlur = 200;

const buildSources = () => {
  if (storage.fallback) {
    return [
      {
        src: buildFileURL("audio/audio.webm"),
        type: "audio/webm",
      },
    ];
  }

  return [
    {
      src: buildFileURL("video/webcams_audio_only.mp4"),
      type: "video/mp4",
    },
    {
      src: buildFileURL("video/webcams.webm"),
      type: "video/webm",
    },
  ].filter((source) => storage.media.find((m) => source.type.includes(m)));
};

const buildTracks = () => {
  return storage.captions.map((lang) => {
    const { locale, localeName } = lang;

    return {
      kind: "captions",
      src: buildFileURL(`caption_${locale}.vtt`),
      srclang: locale,
      label: localeName,
    };
  });
};

const buildOptions = (sources, tracks) => {
  return {
    controlBar: {
      fullscreenToggle: false,
      pictureInPictureToggle: false,
      volumePanel: {
        inline: false,
        vertical: true,
      },
    },
    controls: true,
    fill: true,
    inactivityTimeout: 0,
    playbackRates: config.rates,
    sources: sources.current,
    tracks: tracks.current,
    plugins: {
      seekButtons: {
        forward: shortcuts.seek.seconds,
        back: shortcuts.seek.seconds,
      },
    },
  };
};

const Webcams = () => {
  const intl = useIntl();
  const sources = useRef(buildSources());
  const tracks = useRef(buildTracks());
  const element = useRef();
  const interval = useRef();
  let location = useLocation();
  const search = location.search;
  const locationHideStudentFace = new URLSearchParams(search).get(
    "auto_hide_student_face"
  );
  const isHideStudentFace =
    locationHideStudentFace?.toLocaleLowerCase() === "true";
  const [position, setPosition] = useState(POSITION_BLUR.NONE);
  const { data } = storage;
  const studentWebcamInfo = data?.events?.studentWebcamInfo;

  const [mutedVideo, setMuteVideo] = useState(false);

  const dispatchTimeUpdate = (time) => {
    const event = new CustomEvent(EVENTS.TIME_UPDATE, { detail: { time } });
    document.dispatchEvent(event);
  };

  const setPositionFunc = (data) => {
    if (data?.indexWebcam === -1 || !data?.countWebcam) {
      return setPosition(POSITION_BLUR.NONE);
    }
    if (data?.countWebcam === 1) {
      return setPosition(POSITION_BLUR.FULL);
    }
    if (data?.indexWebcam === 1) {
      return setPosition(POSITION_BLUR.RIGHT);
    }
    setPosition(POSITION_BLUR.LEFT);
  };

  useEffect(() => {
    if (!isHideStudentFace || isEmpty(studentWebcamInfo)) return;
    const _dataWebcam = studentWebcamInfo?.[0];
    setPositionFunc(_dataWebcam);
  }, [isHideStudentFace, studentWebcamInfo]);

  useEffect(() => {
    if (!player.webcams) {
      const video = element.current;
      if (!video) return;

      player.webcams = videojs(video, buildOptions(sources, tracks), () => {
        player.webcams.on("play", () => {
          const frequency = getFrequency();
          interval.current = setInterval(() => {
            const currentTime = player.webcams.currentTime();
            /**
             * Handle blur webcam student
             */
            const reverseArray = [...studentWebcamInfo].reverse();
            const webcamInfo = reverseArray?.find(
              (_item) =>
                currentTime * 1000 >= _item?.timestampWebcam - mlsShowEarlyBlur
            );
            if (isHideStudentFace && !isEmpty(webcamInfo)) {
              setPositionFunc(webcamInfo);
            }

            const playbackRates = player?.webcams?.cache_?.lastPlaybackRate;
            dispatchTimeUpdate(currentTime);
            EventEmitter.dispatch(EVENT_KEY.TIME_UPDATE, currentTime);
            EventEmitter.dispatch(EVENT_KEY.TIME_UPDATE_WEBCAMS, currentTime);
            EventEmitter.dispatch(EVENT_KEY.PLAYBACK_RATE, playbackRates);
            EventEmitter.dispatch(
              EVENT_KEY.PLAYBACK_RATE_WEBCAMS,
              playbackRates
            );
          }, 1000 / (frequency ? frequency : config.rps));
        });

        player.webcams.on("pause", () => {
          EventEmitter.dispatch(EVENT_KEY.PAUSE_VIDEO);
          EventEmitter.dispatch(EVENT_KEY.PAUSE_WEBCAMS);
          clearInterval(interval.current);
        });

        player.webcams.on("seeked", () => {
          const currentTime = player.webcams.currentTime();
          dispatchTimeUpdate(currentTime);
          EventEmitter.dispatch(EVENT_KEY.TIME_SEEK, currentTime);
          EventEmitter.dispatch(EVENT_KEY.TIME_SEEK_WEBCAMS, currentTime);
        });

        const time = getTime();

        EventEmitter.subscribe(EVENT_KEY.MUTE_VIDEO, (muted) => {
          setMuteVideo(muted);
        });

        if (time) {
          player.webcams.on("loadedmetadata", () => {
            const duration = player.webcams.duration();
            if (time < duration) {
              player.webcams.currentTime(time);
            }
          });
        }
      });
      logger.debug(ID.WEBCAMS, "mounted");
    }
  }, [isHideStudentFace, studentWebcamInfo]);

  useEffect(() => {
    return () => {
      if (player.webcams) {
        player.webcams.dispose();
        player.webcams = null;
        logger.debug(ID.WEBCAMS, "unmounted");
      }

      EventEmitter.unsubscribe(EVENT_KEY.DURATION_YOUTUBE);
    };
  }, []);

  return (
    <div
      aria-label={intl.formatMessage(intlMessages.aria)}
      className="webcams-wrapper"
      id={ID.WEBCAMS}
    >
      {!!isHideStudentFace && (
        <div
          className={
            position === POSITION_BLUR.FULL
              ? "blur-webcam blur-webcam--full"
              : "blur-webcam"
          }
          style={{
            left: position === POSITION_BLUR.LEFT ? 0 : "",
            right: position === POSITION_BLUR.RIGHT ? 0 : "",
            display: position === POSITION_BLUR.NONE ? "none" : "block",
            width: position === POSITION_BLUR.FULL ? "100%" : "50%",
          }}
        />
      )}
      <div data-vjs-player>
        <video
          className="video-js"
          playsInline
          preload="auto"
          ref={element}
          muted={mutedVideo}
        />
      </div>
    </div>
  );
};

// Avoid re-render
const areEqual = () => true;

export default React.memo(Webcams, areEqual);
